import { useEffect, useRef } from 'react';

/**
 * Enhanced accessibility hook for mobile menu
 * Phase 4.3: Accessibility Improvements
 * 
 * Features:
 * - Focus trap management
 * - Keyboard navigation support
 * - ARIA live announcements
 * - Reduced motion support
 */
export const useMobileMenuAccessibility = (isOpen: boolean) => {
  const previousFocusRef = useRef<HTMLElement | null>(null);

  // Focus management
  useEffect(() => {
    if (isOpen) {
      // Store the element that had focus before menu opened
      previousFocusRef.current = document.activeElement as HTMLElement;
      
      // Announce menu opening to screen readers
      announceToScreenReader('Navigation menu opened');
    } else {
      // Restore focus when menu closes
      if (previousFocusRef.current) {
        previousFocusRef.current.focus();
      }
      
      // Announce menu closing to screen readers
      announceToScreenReader('Navigation menu closed');
    }
  }, [isOpen]);

  // Handle Escape key to close menu
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        // The Sheet component handles this, but we announce it
        announceToScreenReader('Menu closed with Escape key');
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  return {
    previousFocusRef,
  };
};

/**
 * Announce messages to screen readers
 */
const announceToScreenReader = (message: string) => {
  // Check if announcement element exists, create if not
  let announcer = document.getElementById('mobile-menu-announcer');
  
  if (!announcer) {
    announcer = document.createElement('div');
    announcer.id = 'mobile-menu-announcer';
    announcer.setAttribute('role', 'status');
    announcer.setAttribute('aria-live', 'polite');
    announcer.setAttribute('aria-atomic', 'true');
    announcer.className = 'sr-only';
    document.body.appendChild(announcer);
  }
  
  // Clear and set new message
  announcer.textContent = '';
  setTimeout(() => {
    announcer!.textContent = message;
  }, 100);
};

/**
 * Check if user prefers reduced motion
 */
export const prefersReducedMotion = (): boolean => {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};
